import React, { useState, useEffect, useCallback } from "react";
import api from "../api/homeapi";
import { StaffFrame, DataTable, PageHead, SearchBox, ClassChip, fmtPrice, T } from "./DashboardShared";
import { formatDateFR } from "../utils/format";
import { useNotify, errorMessage } from "../components/Notify";
import { PlusIcon, TrashIcon, XIcon } from "../components/Icons";

const EMPTY = { trainNumber: "", travelClass: "ECONOMIQUE", departureId: "", arrivalId: "", departureDateTime: "", arrivalDateTime: "", price: "", seats: "" };

export default function AdminSchedules() {
  const notify = useNotify();
  const [schedules, setSchedules] = useState([]);
  const [stations,  setStations]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [search,    setSearch]    = useState("");
  const [form, setForm] = useState(EMPTY);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sch, sta] = await Promise.all([api.get("/api/trips"), api.get("/cities")]);
      setSchedules(Array.isArray(sch.data) ? sch.data : []);
      setStations(Array.isArray(sta.data) ? sta.data : []);
    } catch (e) {
      notify.error(errorMessage(e, "Impossible de charger les voyages."));
    } finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    const missing = [];
    if (!form.trainNumber.trim()) missing.push("n° de train");
    if (!form.departureId) missing.push("gare de départ");
    if (!form.arrivalId) missing.push("gare d'arrivée");
    if (!form.departureDateTime) missing.push("date de départ");
    if (!form.price) missing.push("prix");
    if (missing.length) { notify.warning(`Renseignez : ${missing.join(", ")}.`, { title: "Formulaire incomplet" }); return; }
    if (form.departureId === form.arrivalId) { notify.warning("Les gares de départ et d'arrivée doivent être différentes."); return; }
    if (form.arrivalDateTime && form.arrivalDateTime <= form.departureDateTime) { notify.warning("L'arrivée doit être postérieure au départ."); return; }

    setSaving(true);
    try {
      await api.post("/api/trips", form);
      setShowForm(false);
      setForm(EMPTY);
      loadData();
      notify.success(`Le voyage ${form.trainNumber} a été créé.`, { title: "Voyage créé" });
    } catch (e) {
      notify.error(errorMessage(e, "La création du voyage a échoué."), { title: "Création impossible" });
    } finally { setSaving(false); }
  };

  const handleDelete = async (t) => {
    const ok = await notify.confirm({
      title: "Supprimer ce voyage ?",
      danger: true,
      confirmLabel: "Supprimer",
      message: <>Le voyage <strong>{t.trainNumber}</strong> ({t.departure} → {t.arrival}, {formatDateFR(t.date)} {t.departureTime}) sera définitivement supprimé.</>,
    });
    if (!ok) return;
    try {
      await api.delete(`/api/trips/${t.id}`);
      notify.success(`Le voyage ${t.trainNumber} a été supprimé.`);
      loadData();
    } catch (e) {
      notify.error(errorMessage(e, "La suppression a échoué."), { title: "Suppression impossible" });
    }
  };

  const q = search.trim().toLowerCase();
  const rows = q ? schedules.filter(t => `${t.trainNumber} ${t.departure} ${t.arrival}`.toLowerCase().includes(q)) : schedules;

  return (
    <StaffFrame>
      <div className="page">
        <PageHead title="Gestion des horaires" sub="Voyages disponibles sur la ligne Transgabonais">
          <SearchBox value={search} onChange={setSearch} placeholder="Train ou gare…" />
          <button className={`btn ${showForm ? "btn-ghost" : "btn-primary"}`} onClick={() => setShowForm(v => !v)}>
            {showForm ? <><XIcon size={14}/> Fermer</> : <><PlusIcon size={14}/> Nouveau voyage</>}
          </button>
        </PageHead>

        {showForm && (
          <div className="stf-card" style={{ animation: "fadeUp .3s ease both" }}>
            <div className="stf-card-title">Créer un voyage</div>
            <div className="stf-card-sub">Les champs marqués d'un * sont obligatoires.</div>
            <div className="form-grid">
              <div><label className="dlabel">N° Train *</label><input className="dinput" placeholder="TR-101" value={form.trainNumber} onChange={e => set("trainNumber", e.target.value)} /></div>
              <div><label className="dlabel">Classe</label>
                <select className="dselect" value={form.travelClass} onChange={e => set("travelClass", e.target.value)}>
                  <option value="ECONOMIQUE">Économique</option>
                  <option value="PREMIERE">1ère Classe</option>
                  <option value="VIP">VIP</option>
                </select>
              </div>
              <div><label className="dlabel">Gare de départ *</label>
                <select className="dselect" value={form.departureId} onChange={e => set("departureId", e.target.value)}>
                  <option value="">Choisir…</option>
                  {stations.map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
                </select>
              </div>
              <div><label className="dlabel">Gare d'arrivée *</label>
                <select className="dselect" value={form.arrivalId} onChange={e => set("arrivalId", e.target.value)}>
                  <option value="">Choisir…</option>
                  {stations.map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
                </select>
              </div>
              <div><label className="dlabel">Départ (date + heure) *</label><input className="dinput" type="datetime-local" value={form.departureDateTime} onChange={e => set("departureDateTime", e.target.value)} /></div>
              <div><label className="dlabel">Arrivée (date + heure)</label><input className="dinput" type="datetime-local" min={form.departureDateTime || undefined} value={form.arrivalDateTime} onChange={e => set("arrivalDateTime", e.target.value)} /></div>
              <div><label className="dlabel">Prix (FCFA) *</label><input className="dinput" type="number" inputMode="numeric" min="0" placeholder="12500" value={form.price} onChange={e => set("price", e.target.value)} /></div>
              <div><label className="dlabel">Nombre de places</label><input className="dinput" type="number" inputMode="numeric" min="1" placeholder="60" value={form.seats} onChange={e => set("seats", e.target.value)} /></div>
            </div>
            <div className="btn-row" style={{ marginTop: 18 }}>
              <button className="btn btn-primary btn-lg" onClick={handleCreate} disabled={saving}>{saving ? "Création…" : "Créer le voyage"}</button>
              <button className="btn btn-ghost btn-lg" onClick={() => { setShowForm(false); setForm(EMPTY); }}>Annuler</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loading-block"><div className="spinner"/>Chargement des voyages…</div>
        ) : (
          <DataTable stack="md" rows={rows} empty={q ? "Aucun voyage ne correspond à votre recherche." : "Aucun voyage programmé."} columns={[
            { key: "train", label: "Train", main: true, render: t => (
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span className="mono" style={{ fontWeight: 700, color: T.forest }}>{t.trainNumber}</span>
                <span style={{ fontWeight: 600 }}>{t.departure}<span style={{ color: T.smoke, margin: "0 5px" }}>→</span>{t.arrival}</span>
              </div>) },
            { key: "dep", label: "Départ", nowrap: true, render: t => <span className="mono">{formatDateFR(t.date)} {t.departureTime}</span> },
            { key: "arr", label: "Arrivée", nowrap: true, render: t => <span className="mono">{t.arrivalTime || "-"}</span> },
            { key: "class", label: "Classe", render: t => <ClassChip value={t.travelClass}/> },
            { key: "price", label: "Prix", render: t => <span className="money">{fmtPrice(t.price)}</span> },
            { key: "seats", label: "Places", render: t => <span><strong style={{ color: t.seats <= 5 ? T.rust : T.forest }}>{t.seats}</strong><span className="muted small"> / {t.totalSeats}</span></span> },
            { key: "bk", label: "Résas", render: t => <strong>{t.bookingsCount || 0}</strong> },
            { key: "act", label: "Actions", actions: true, render: t => (
              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(t)}><TrashIcon size={13}/> Supprimer</button>) },
          ]}/>
        )}
      </div>
    </StaffFrame>
  );
}
